import {OdpSegmentCache} from '../utils/odp';

/**
 * Load/fetch Visitor Profile
 * @module
 */
var _ = require('optimizely-client-fn');
var Flux = require('../flux');

var actionTypes = require('../constants/action_types');
var Behavior = require('../behavior');
var BehaviorStore = require('../behavior/store');
var Cookie = require('../utils/cookie');
var DateEnv = require('../env/date');
var di = require('../di');
var enums = require('../constants/enums');
var JSONEnv = require('../lib/json');
var KeyPath = require('../utils/key_path');
var LocalStorage = require('../env/local_storage').LocalStorage;
var Logger = require('../utils/logger');
var PromiseEnv = require('../lib/promise').Promise;
var Query = require('../utils/query');
var StorageKeys = require('../constants/enums').VisitorStorageKeys;
var WindowEnv = require('../env/window');
var XDomainStorage;
if (__XDOMAIN__) {
  XDomainStorage = require('../utils/xdomain_storage');
}

var CookieOptionStore = di.get('stores/cookie_options');
var GlobalStore = di.get('stores/global');
var LayerStore = di.get('stores/layer');
var PluginsStore = di.get('stores/plugins');
var RumStore = di.get('stores/rum');
var SessionStore = di.get('stores/session');
var VisitorIdStore = di.get('stores/visitor_id');
var VisitorBucketingStore = di.get('stores/visitor_bucketing');
var VisitorStore = di.get('stores/visitor');
var ProviderStatusStore = di.get('stores/provider_status');

var EXPERIMENTAL_EDGE_SPA = __EXPERIMENTAL_EDGE_SPA__;
var EXPERIMENTAL_WEB_SDK = __EXPERIMENTAL_WEB_SDK__;

/**
 * @returns {VisitorId}
 *        The current ID, if it exists.
 *        Otherwise, a freshly generated ID.
 */
exports.getOrGenerateId = function(): VisitorId {
  return {
    randomId: exports.getCurrentId() || generateRandomVisitorId(),
  };
};

/**
 * Prioritization of visitor id usage, which moves down if that option is null:
 * 1. API.v1 visitorId (Edge only)
 * 2. visitorIdLocator
 * 3. OEUID Cookie
 *
 * @return {string|undefined} current ID for the visitor, or undefined if no ID set
 */
exports.getCurrentId = function(): string|undefined {
  var visitorIdLocator = VisitorIdStore.getVisitorIdLocator();

  return VisitorStore.getVisitorIdFromAPI() ||
        (visitorIdLocator ? generateVisitorIdFromLocator(visitorIdLocator)
        : Cookie.get(enums.COOKIES.VISITOR_ID));
};

/**
 * @return {boolean} whether there is any (optimizely) data at all on the current origin
 */
exports.hasSomeData = function(): boolean {
  return LocalStorage.keys().length > 0;
};

/**
 * Populate VisitorIdStore, reload visitor data and persist cookie if bucketingId changed.
 * @param {VisitorId} visitorId
 */
exports.setId = function(visitorId:VisitorId) {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('VisitorId', visitorId);
  }

  var existingVisitorId = VisitorIdStore.getBucketingId();

  // set visitor ID in store
  Flux.dispatch(actionTypes.SET_VISITOR_ID, visitorId);

  // check if changed
  if (VisitorIdStore.getBucketingId() !== existingVisitorId) {
    // get visitor data into stores
    loadData();

    // Delete irrelevant localStorage keys stored in the current origin
    exports.deleteOldLocalData();

    // Delete irrelevant localStorage keys that are owned by the current origin and stored in the sync origin
    if (__XDOMAIN__){
      XDomainStorage.deleteData(visitorId);
    }
  }

  try {
    // Existence of a visitorIdLocator indicates the customer provided a visitorId.
    // Edge delivery already set the cookie on the header, avoid setting it again.
    if (!VisitorIdStore.getVisitorIdLocator() && !EXPERIMENTAL_WEB_SDK) {
      exports.maybePersistVisitorId(visitorId);
    }
  } catch(e) {
    Logger.error('Visitor / Unable to persist visitorId, disabling tracking');
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      trackingDisabled: true,
    });

    // Reduce the cardinality of MismatchError messages
    if (e instanceof Cookie.MismatchError) {
      Logger.error('Visitor / Cookie not set to correct value:', e);
      throw new Error('Cookie mismatch error while persisting visitorId');
    }

    // Just rethrow others
    throw e;
  }
  // refresh the user sessionId if necessary
  exports.refreshSession();
};

// TODO(tyler): throw if invalid
/**
 * Get the variationIdMap from local storage
 */
exports.getVariationIdMap = function() {
  if( EXPERIMENTAL_WEB_SDK) {
    const { getCurrentStickyEdgeData, STICKY_STORAGE_KEY } = require('web_sdk');
    return getCurrentStickyEdgeData(STICKY_STORAGE_KEY.VARIATION_MAP) || {};
  }
  // Should we ignore localstorage data? or merged it?
  return getCurrentNamespaceVisitorData(StorageKeys.VARIATION_MAP) || {};
};

/**
 * Update variationIdMap in store, for use with variation sticky bucketing
 * @param {string} layerId
 * @param {string} experimentId
 * @param {string} variationId
 */
exports.updateVariationIdMap = function(layerId:string, experimentId:string, variationId:string) {
  Flux.dispatch(actionTypes.UPDATE_VARIATION_ID_MAP, {
    layerId: layerId,
    experimentId: experimentId,
    variationId: variationId,
  });
};

/**
 * Update variationIdMap in local storage
 */
exports.persistVariationIdMap = function() {
  var variationIdMapStr = VisitorBucketingStore.getVariationIdMapString();
  persistVisitorData(StorageKeys.VARIATION_MAP, variationIdMapStr, true);

  if(EXPERIMENTAL_WEB_SDK) {
    const { STICKY_STORAGE_KEY, setCurrentStickyEdgeData } = require('web_sdk');
    setCurrentStickyEdgeData(STICKY_STORAGE_KEY.VARIATION_MAP, VisitorBucketingStore.getVariationIdMap());
  }
};

/**
 * Get the contextualMabMap from local storage
 */
exports.getContextualMabMap = function() {
  return getCurrentNamespaceVisitorData(StorageKeys.CONTEXTUAL_MAB) || {};
};

/**
 * Update contextualMabMap in store
 * @param {string} layerId
 * @param {string} experimentId
 * @param {CmabVariationStorageData} variationData
 */
exports.updateContextualMabMap = function(layerId: string, experimentId: string, variationData: CmabVariationStorageData) {
  Flux.dispatch(actionTypes.UPDATE_CONTEXTUAL_MAB_MAP, {
    layerId: layerId,
    experimentId: experimentId,
    variationData: variationData,
  });
};

/**
 * Update contextualMabMap in local storage
 */
exports.persistContextualMabMap = function() {
  var contextualMabMapStr = VisitorBucketingStore.getContextualMabMapString();
  persistVisitorData(StorageKeys.CONTEXTUAL_MAB, contextualMabMapStr, true);
};

/**
 * Get the ODP segments from localstorage
 * */
exports.getOdpSegments = function () {
  return getCurrentNamespaceVisitorData(StorageKeys.ODP_SEGMENTS) || {};
};

/**
 * Persist ODP segments into local storage
 */
exports.persistOdpSegments = function(segmentCache: OdpSegmentCache) {
    persistVisitorData(StorageKeys.ODP_SEGMENTS, segmentCache, false);
};
/**
 * Get the preferredLayerMap from local storage
 */
function getPreferredLayerMap() {
  return getCurrentNamespaceVisitorData(StorageKeys.LAYER_MAP) || {};
}

/**
 * Update preferredLayerMap in store, for use with group sticky bucketing
 * @param {string} groupId
 * @param {string} layerId
 */
function updatePreferredLayerMap(groupId:string, layerId:string) {
  Flux.dispatch(actionTypes.UPDATE_PREFERRED_LAYER_MAP, {
    groupId: groupId,
    layerId: layerId,
  });
}

/**
 * Update preferredLayerMap in local storage
 */
function persistPreferredLayerMap() {
  var preferredLayerMap = VisitorBucketingStore.getPreferredLayerMapString();
  persistVisitorData(StorageKeys.LAYER_MAP, preferredLayerMap, true);
}

if (__GROUPS__) {
  exports.getPreferredLayerMap = getPreferredLayerMap;
  exports.updatePreferredLayerMap = updatePreferredLayerMap;
}

/**
 * Store persistableData in localStorage for later loading into tracker_optimizely store
 * @param {Object} persistableData
 */
exports.persistTrackerOptimizelyData = function(persistableData:any) {
  // TODO(tyler): remove data if empty
  persistVisitorData(StorageKeys.TRACKER_OPTIMIZELY, persistableData);
};

function loadTrackerOptimizelyData(persistableData) {
  Flux.dispatch(actionTypes.SET_TRACKER_PERSISTABLE_STATE, persistableData);
}

/**
 * Denotes that an event occurred where the visitor's session should be extended
 */
exports.refreshSession = function() {
  Flux.dispatch(actionTypes.REFRESH_SESSION);
};

/**
 * Filters visitor data providers by isLazy being falsy and populates them
 * @param {VisitorProfileProvider[]} providers
 * @param {module:stores/visitor~VisitorProfile} existingVisitorProfile
 * @return {Promise}
 */
exports.populateEagerVisitorData = function(providers:VisitorProfileProvider[], existingVisitorProfile:import('api/core_modules').VisitorProfile): Promise<any> {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('populateEagerVisitorData');
  var eagerProviders = _.filter(providers, function(provider) {
    return !provider.isLazy;
  });
  var visitorData = exports.populateVisitorData(eagerProviders, existingVisitorProfile);
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('populateEagerVisitorData');
  return visitorData;
};

/**
 * Filters visitor data providers by isLazy being truthy and fetches them
 * @param {VisitorProfileProvider[]} providers
 * @param {module:stores/visitor~VisitorProfile} existingVisitorProfile
 * @param {Set<string>} uniqueAudienceTypes
 * @return {Promise}
 */
exports.populateLazyVisitorData = function(providers:VisitorProfileProvider[], existingVisitorProfile:import('api/core_modules').VisitorProfile, uniqueAudienceTypes: Set<string>): Promise<any> {
  Logger.debug('Populating lazy visitor. List of all providers', providers);

  // As part of the CJS-7577 effort, we are now checking for lazy providers that are available in the audience conditions.
  var filteredLazyProviders = _.filter(providers, function(provider) {
    return provider.isLazy && uniqueAudienceTypes.has(provider.provides);
  });

  Logger.debug('Filtered list of providers that are lazy and present in audiences', filteredLazyProviders);

  return exports.populateVisitorData(filteredLazyProviders, existingVisitorProfile);
};

exports.populateVisitorData = function(providers, existingVisitorProfile) {
  existingVisitorProfile = existingVisitorProfile || {};
  var evaluate = _.partial(evaluateVisitorProfileProvider, existingVisitorProfile);

  // Run async providers, queue sync providers
  var promises = _(providers)
    .filter({
      isAsync: true
    })
    .map(evaluate)
    .filter()
    .value();

  // Run sync providers
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('visitorSyncProviders');
  _.forEach(_.filter(providers, function(provider) {
    return !provider.isAsync;
  }), evaluate);
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('visitorSyncProviders');

  if (promises.length > 0) {
    return PromiseEnv.all(promises);
  } else {
    return PromiseEnv.resolve();
  }
};

/**
 * Evaluate a single provider.
 *
 * Evaluation is skipped if either:
 * - an Edge microsnippet provided a value for this attribute already
 * - the attribute is sticky and already has a value
 *
 * @param {module:stores/visitor~VisitorProfile} existingVisitorProfile
 * @param {VisitorProfileProvider} provider
 * @returns {Promise|undefined}
 * If evaluation is skipped as described above or the given provider is synchronous,
 * undefined is returned. Otherwise, the given async provider is run and a promise is
 * returned that surely fulfills with undefined.
 * @private
 */
function evaluateVisitorProfileProvider(existingVisitorProfile:import('api/core_modules').VisitorProfile, provider:VisitorProfileProvider): Promise<any>|undefined {
  if (!provider.getter) {
    // Expect provider value to be defined some other way (e.g. API)
    return;
  }

  // Just a shortcut
  function addToVisitorAttributes(key, value) {
    var timestamp;
    // To save space, only set the timestamp for providers with an attributionType
    if (provider.attributionType) {
      timestamp = DateEnv.now();
    }
    Flux.dispatch(actionTypes.SET_VISITOR_ATTRIBUTES, {
      attributes: [{
        key: key,
        value: value,
        metadata: {lastModified: timestamp},
      }],
    });
  }

  var visitorAttributeKey = provider.provides;
  // Backwards compatibility with old plugins
  if (!_.isArray(visitorAttributeKey)) {
    visitorAttributeKey = [visitorAttributeKey as string];
  }

  if (EXPERIMENTAL_EDGE_SPA) {
    // todo(CJS-3869): Add IP to values we check once edge-decider is always able to calculate IP
    // Since IP and location is only sometimes handed off, we don't know if the value here is from the edge-decider
    // or a potentially stale one from local storage, so we don't check it here and always (re)calculate it.
    if (_.includes(['queryParams', 'url'], visitorAttributeKey[0])) {
        return;
    }
  }

  var stickyAndDefined = provider.isSticky && !_.isUndefined(KeyPath.getFieldValue(existingVisitorProfile, visitorAttributeKey));
  if (stickyAndDefined) {
    return;
  }

  var promise;

  try {
    var evaluatedValue = di.evaluate(provider.getter);
    if (_.isFunction(evaluatedValue)) {
      // provider has returned a function - call it providing functions
      // to get/set value to/from storage
      evaluatedValue = evaluatedValue(
        function getCurrentProviderValue() {
          return KeyPath.getFieldValue(existingVisitorProfile, visitorAttributeKey);
        },
        function setProviderValue(value) {
          addToVisitorAttributes(visitorAttributeKey, value);
        }
      );
    }
    if (!_.isUndefined(evaluatedValue)) {
      if (provider.isAsync) {
        promise = evaluatedValue.then(function (result) {
          addToVisitorAttributes(visitorAttributeKey, result);
        }, function(e) {
          Logger.warn('Failed to evaluate provider for "' + provider.provides + '"; error was:', e);
        });
        Flux.dispatch(actionTypes.SET_VISITOR_ATTRIBUTE_PENDING, {
          key: visitorAttributeKey,
          pending: promise,
        });
      } else {
        // Save value to the store
        addToVisitorAttributes(visitorAttributeKey, evaluatedValue);
      }
    }
  } catch (e) {
    // TODO(tyler): reimplement this in Controller
    if (__PREVIEW__) {
      // Since preview includes all plugins, it's expected that many of them will fail
      Logger.debug('Failed to evaluate getter for provider:', provider);
    } else {
      Logger.warn('Failed to evaluate getter for provider for "' + provider.provides + '"; error was: ' + e.message);
    }
  }

  return promise;
}

/**
 * Load data for current visitor ID from localStorage into stores.
 * @private
 */
function loadData() {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('viewLoadData');
  loadBehaviorEvents(
    getCurrentNamespaceVisitorData(StorageKeys.EVENTS) || [],
    getCurrentNamespaceVisitorData(StorageKeys.EVENT_QUEUE) || []);

  // Get layer states for all namespaces on the current account
  var allLayerStateData = getVisitorDataFromAllNamespaces(StorageKeys.LAYER_STATES);
  _.forEach(allLayerStateData, function (dataObj) {
    dataObj.item = _.map(dataObj.item, deserializeLayerState);
  });

  // Enrich them with namespace and load them
  loadLayerStates(enrichLayerStates(allLayerStateData));
  loadSessionState(getCurrentNamespaceVisitorData(StorageKeys.SESSION_STATE) || {});
  loadVisitorProfile(getCurrentNamespaceVisitorData(StorageKeys.VISITOR_PROFILE) || {});

  if (__TRACK_EVENTS_PERSIST_PENDING__) {
    var trackerOptimizelyData = getCurrentNamespaceVisitorData(StorageKeys.TRACKER_OPTIMIZELY);
    if (trackerOptimizelyData) {
      loadTrackerOptimizelyData(trackerOptimizelyData);
    }
  }

  // Must come after loadLayerStates because loadLayerStates does not merge
  exports.loadForeignData();

  exports.removeLegacySessionStateCookies();
  __PROFILE__ && require('core/env/performance').timeEnd('viewLoadData');
}

/**
 * @param {string} key
 *        One of enums.VisitorStorageKeys.
 * @returns {Object}
 *        The deserialized value of the local item for the given key and
 *        current namespace-ID.
 */
function getCurrentNamespaceVisitorData(key:string): any {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('visitorData');
  var visitorDataKey = getVisitorDataKey(key);
  var item = LocalStorage.getItem(visitorDataKey);
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('visitorData');

  if (_.isString(item)) {
    item = safeParse(item);
  }

  return item;
}

/**
 * Adds the namespace to the layerState
 * @param {Array<Object>} layerStateData
 * @returns {LayerState[]}
 * @private
 */
function enrichLayerStates(layerStateData:any[]): LayerState[] {
  var result = [];
  _.each(layerStateData, function(dataObj) {
    _.each(dataObj.item, function(layerState) {
      layerState.namespace = dataObj.namespace;
      result.push(layerState);
    });
  });
  return result;
}

/**
 *
 * @param {PersistableLayerState|LayerState} persistedLayerState
 * @return {LayerState}
 * @private
 */
function deserializeLayerState(persistedLayerState:PersistableLayerState|LayerState): LayerState {
  var res;

  if ((persistedLayerState as LayerState).layerId) {
    // Backwards compatibility: if the persisted layer state is a "full" layer state, just return it
    res = persistedLayerState;
  } else {
    let minimizedLayerState:PersistableLayerState = persistedLayerState as PersistableLayerState;
    res = {
      layerId: minimizedLayerState.i,
      pageId: minimizedLayerState.p,
      decisionTimestamp: minimizedLayerState.t,
      decisionTicket: {
        audienceIds: minimizedLayerState.a || [],
      },
      decision: {
        layerId: minimizedLayerState.i,
        experimentId: minimizedLayerState.x || null,
        variationId: minimizedLayerState.v || null,
        isLayerHoldback: minimizedLayerState.h || false,
      },
    };
  }

  if (__INVARIANT__) {
    require('../utils/invariant').isType('LayerState', res);
  }

  return res;
}

/**
 * Note: Assume that if two items are associated with the same ID, then they come from snippets
 * that belong to the same Optimizely account. In this context, this means assume that
 * any/all items with the current ID belong to the current account.
 *
 * @param {string} key
 *        One of enums.VisitorStorageKeys.
 * @returns {Array.<Object>}
 *        Objects describing each of the items (local and foreign) associated with the current
 *        visitor ID and given key. It's assumed the items all belong to the current account.
 * @private
 */
function getVisitorDataFromAllNamespaces(key:string): any[] {
  var userId = VisitorIdStore.getBucketingId();
  var data = [];
  var regexString = userId + '\\$\\$([^$]+?)\\$\\$' + key;
  var regex = new RegExp(regexString);
  _.each(LocalStorage.keys(), function(storageKey) {
    var matchData = storageKey.match(regex);
    if (matchData) {
      var dataObj = {
        namespace: matchData[1],
        userId: userId,
        item: safeParse(LocalStorage.getItem(storageKey)),
      };

      data.push(dataObj);
    }
  });
  return data;
}

/**
 * Persist the given events to the local EVENTS item.
 * @param {BehaviorCompactEvent[]} persistentEvents
 */
exports.persistBehaviorEvents = function(persistentEvents:BehaviorCompactEvent[]) {
  persistVisitorData(StorageKeys.EVENTS, persistentEvents);
};

/**
 * Persist the given events to the local EVENT_QUEUE item.
 * @param {BehaviorCompactEvent[]} persistentEventQueue
 */
exports.persistBehaviorEventQueue = function(persistentEventQueue:BehaviorCompactEvent[]) {
  persistVisitorData(StorageKeys.EVENT_QUEUE, persistentEventQueue);
};

/**
 * Get the total length of the _persisted_ behavior EVENTS, EVENT_QUEUE
 * _without_ updating the BehaviorStore. This is an expensive operation so use
 * it sparingly.
 * @return {number}
 */
exports.getPersistedBehaviorEventCount = function(): number {
  var events = getCurrentNamespaceVisitorData(StorageKeys.EVENTS) || [];
  var eventQueue = getCurrentNamespaceVisitorData(StorageKeys.EVENT_QUEUE) || [];
  return BehaviorStore.deserialize(events).length + BehaviorStore.deserialize(eventQueue).length;
};

/**
 * Persist LayerStore layer states from the current namespace to localStorage
 */
exports.persistLayerStates = function() {
  var layerStates = LayerStore.getLayerStates(exports.getNamespace());

  // remove namespace field before save
  layerStates = _.map(layerStates, function(layerState) {
    return _.omit(layerState, 'namespace');
  });

  persistVisitorData(StorageKeys.LAYER_STATES, layerStates);
};

/**
 * Persist SessionStore session state to localStorage
 */
exports.persistSessionState = function() {
  persistVisitorData(StorageKeys.SESSION_STATE, SessionStore.getState());
};

/**
 * Persist VisitorProfile to localStorage
 */
exports.persistVisitorProfile = function() {
  const visitorProfile = serializeVisitorProfile();
  persistVisitorData(StorageKeys.VISITOR_PROFILE, visitorProfile);

  if(EXPERIMENTAL_WEB_SDK) {
    const { STICKY_STORAGE_KEY, setCurrentStickyEdgeData } = require('web_sdk');
    setCurrentStickyEdgeData(
      STICKY_STORAGE_KEY.VISITOR_PROFILE,
      filterVisitorStickyProfile(visitorProfile)
    );
  }
};

/**
 * Persist Visitor Id store to localstorage
 */
exports.persistVisitorBucketingStore = function() {
  exports.persistVariationIdMap();
  if(__GROUPS__) {
    persistPreferredLayerMap();
  }
  exports.persistContextualMabMap();
};

/**
 * Persist visitor data to local storage
 * @param {string} key
 * @param {Object|Array|string} value
 * @param {boolean} [isString]
 * @private
 */
function persistVisitorData(key:string, value:any|Array<any>|string, isString?:boolean) {
  try {
    var visitorDataKey = getVisitorDataKey(key);
    if (!isString) {
      value = JSONEnv.stringify(value);
    }
    try {
      LocalStorage.setItem(visitorDataKey, value);
    } catch(e) {
      Logger.warn('Visitor / Unable to set localStorage key, error was:', e);
      throw new Error('Unable to set localStorage');
    }
    if (__XDOMAIN__) {
      // TODO(tyler): better way to declare this dependency
      XDomainStorage.setItem(visitorDataKey, value);
    }
  } catch(e) {
    // TODO(tyler): add a lifecycle event for postVisitorProfile load so we don't lose it
    Logger.warn('Unable to persist visitor data:', e.message);
  }
}

function getVisitorDataKey(key) {
  var userId = VisitorIdStore.getBucketingId();
  if (!userId) {
    throw new Error('Visitor bucketingId not set');
  }
  var namespace = exports.getNamespace();
  if (!namespace) {
    throw new Error('Namespace is not set');
  }
  return [userId, namespace, key].join('$$');
}

/**
 * Gets userId from a xdomain storage foreign key matching the current namespace
 * XDomain storage keys have the format <origin>_<userId>$$<namespace>$$<keyName>
 * @param {string} key
 * @param {string} origin
 * @returns {string|undefined}
 */
exports.getUserIdFromKey = function(key:string, origin:string): string|undefined {
  var userId;
  if (_.includes(key, origin) &&
      _.includes(key, '_') &&
      _.includes(key, '$$') &&
      _.includes(key.slice(key.indexOf('$$')), exports.getNamespace())) {
    userId = key.slice(key.indexOf('_') + 1, key.indexOf('$$'));
  }
  return userId;
};

/**
 * Conditionally write the given visitor ID to the cookie named ${enums.COOKIES.VISITOR_ID}.
 * @param {VisitorId} visitorId
 */
exports.maybePersistVisitorId = function(visitorId:VisitorId) {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('VisitorId', visitorId);
  }

  if (visitorId.randomId) {
    // Refresh cookie if auto-refresh is on or if currently persisted Id (if any) is
    // different than the given visitorId
    if (CookieOptionStore.getAutoRefresh() || exports.getCurrentId() !== visitorId.randomId) {
      Cookie.set(enums.COOKIES.VISITOR_ID, visitorId.randomId);
      Logger.log('Persisting visitorId:', visitorId.randomId);
    } else {
      Logger.log('Not persisting visitorId: value is not changed and also auto-refresh is disabled');
    }
  }
  else if (__INVARIANT__) {
    throw new Error('No ID specified!');
  }
};

/**
 * Get current value for a given field of the visitor profile.
 *
 * @param {string|Array<string>} fieldKey
 * @return {*|undefined}
 */
exports.getAttribute = function(fieldKey:string|string[]): any|undefined {
  return VisitorStore.getAttribute(fieldKey);
};

/**
 * Get pending value for a given attribute
 *
 * @param {string|Array<string>} fieldKey
 * @returns {Promise|undefined}
 */
exports.getPendingAttributeValue = function(fieldKey:string|string[]): Promise<any>|undefined {
  return ProviderStatusStore.getPendingAttributeValue(fieldKey);
};

/**
 * If the given item is data from a foreign origin and for the current visitor identity,
 * absorb its data into the appropriate store.
 *
 * @param {string} key
 *        A storage key.
 * @param {string} value
 *        The current value of the item with the given key.
 * @private
 */
function maybeUpdateLocalStore(key:string, value:string) {
  if (!VisitorIdStore.getBucketingId()) {
    throw new Error('Cannot update local store because bucketingId not set');
  }

  //Check if the key is of foreign origin, ends EXACTLY with one of the keys in enums.AllStorageKeys
  // and belongs to the current visitor before updating data into appropriate store
  if(!isForeignKey(key)) {
    return;
  }
  var storageKey = exports.getStorageKeyFromKey(key);
  if (!_.includes(StorageKeys, storageKey)) {
    return;
  }
  var currentVisitorData = getVisitorDataKey(storageKey);
  if (key.indexOf(currentVisitorData) <= 0) {
    return;
  }

  value = safeParse(value);
  // The parsed value could be null (CJS-4262) or worse (hypothetical).
  if (!value) {
    return;
  }

  if (storageKey === StorageKeys.EVENT_QUEUE) {
    Flux.dispatch(actionTypes.SET_FOREIGN_VISITOR_EVENT_QUEUE, {
      key: key,
      value: BehaviorStore.deserialize(value),
    });
  } else if (storageKey === StorageKeys.EVENTS) {
    Flux.dispatch(actionTypes.SET_FOREIGN_VISITOR_EVENTS, {
      key: key,
      value: BehaviorStore.deserialize(value),
    });
  } else if (storageKey === StorageKeys.LAYER_STATES) {
    Flux.dispatch(actionTypes.LOAD_PERSISTED_LAYER_STATES, {
      layerStates: _.map(value, deserializeLayerState),
      merge: true,
    });
  } else if (storageKey === StorageKeys.VARIATION_MAP) {
    Flux.dispatch(actionTypes.MERGE_VARIATION_ID_MAP, {
      variationIdMap: value,
    });
  } else if (storageKey === StorageKeys.CONTEXTUAL_MAB) {
    Flux.dispatch(actionTypes.MERGE_CONTEXTUAL_MAB_MAP, {
      contextualMabMap: value,
    });
  } else if (storageKey === StorageKeys.VISITOR_PROFILE) {
    // For custom attributes, resolve which values to use
    // TODO (Lauren): Update to deal with other properties of the visitor profile, not
    // just custom attributes (CJS-541 & CJS-771)
    var shouldReconcile = ['custom'];
    var xDomainVisitorProfile:any = value;
    _.each(shouldReconcile, function(attributeType) {
      var provider = PluginsStore.getPlugin(enums.PluginTypes.visitorProfileProviders, attributeType);
      if (!provider) {
        Logger.debug('Attribute type', attributeType, 'not used by any audiences');
      } else if (xDomainVisitorProfile.profile && xDomainVisitorProfile.metadata) {  // Without those properties xdomainData is in old format - use local store data
        var reconciledValue = resolveConflicts(xDomainVisitorProfile, attributeType, provider.attributionType);
        if (!_.isEmpty(reconciledValue)) {
          // Add them to the visitor store
          var reconciledValues = [];
          _.forOwn(reconciledValue.data, function(propertyValue, propertyKey) {
            var metadata = reconciledValue.metadata[propertyKey];
            var formattedValue = {
              key: [attributeType, propertyKey],
              value: propertyValue,
              metadata: metadata,
            };
            reconciledValues.push(formattedValue);
          });
          Flux.dispatch(actionTypes.SET_VISITOR_ATTRIBUTES, {
            attributes: reconciledValues,
          });
        }
      }
    });
  }
}

/**
 * Resolve conflicts between localStorage data and xdomain storage data
 * @param {Object} data - current visitor profile
 * @param {string} attributeTypeOrName - key for the property in the visitor profile
 * @param {string} attributionType - how to resolve conflicts between local and xdomain data
 * @returns {Object} data and metadata to persist to the visitorStore
 * @private
 */
function resolveConflicts(data:any, attributeTypeOrName:string, attributionType:string): any {
  // TODO (Lauren): Update this function to work with data format of properties other than custom attributes
  var localStoreData = VisitorStore.getAttribute(attributeTypeOrName);
  var localStoreMetadata = VisitorStore.getAttributeMetadata(attributeTypeOrName);
  var xdomainData = data.profile[attributeTypeOrName];
  var xdomainMetadata = data.metadata[attributeTypeOrName];

  if (_.isEmpty(localStoreData)) {
    // If there's nothing in the local store, store the xdomain data
    return {
      data: xdomainData,
      metadata: xdomainMetadata,
    };
  }

  var dataToStore = {} as any;

  _.forOwn(xdomainData, function(propertyValue, propertyName) {
    var localDataTimestamp;
    if (localStoreMetadata && localStoreMetadata[propertyName]) {
      localDataTimestamp = localStoreMetadata[propertyName].lastModified;
    }
    var foreignDataTimestamp;
    if (xdomainMetadata && xdomainMetadata[propertyName]) {
      foreignDataTimestamp = xdomainMetadata[propertyName].lastModified;
    }

    if (attributionType === enums.AttributionTypes.FIRST_TOUCH && localDataTimestamp >= foreignDataTimestamp ||
        attributionType === enums.AttributionTypes.LAST_TOUCH && foreignDataTimestamp >= localDataTimestamp ||
        (_.isUndefined(localDataTimestamp) && foreignDataTimestamp)) {
      // return the xdomain data (includes the case where localDataTimestamp is undefined - property not in store)
      // But if both timestamps undefined, use local store data
      dataToStore.data = dataToStore.data || {};
      dataToStore.data[propertyName] = propertyValue;
      if (foreignDataTimestamp) {
        dataToStore.metadata = dataToStore.metadata || {};
        dataToStore.metadata[propertyName] = dataToStore.metadata[propertyName] || {};
        dataToStore.metadata[propertyName].lastModified = foreignDataTimestamp;
      }
    }
  });

  return dataToStore;
}

/**
 * Check if key is for data from a foreign origin.
 * e.g.
 * oeu123$$namespace$$events => local key
 * http://example.com_oeu123$$namespace$$events => foreign key
 * @param {string} key key to check
 * @returns {boolean}
 */
function isForeignKey(key:string): boolean {
  var firstPart = key.split('$$')[0];
  return firstPart.indexOf('://') > 0;
}
exports.isForeignKey = isForeignKey;

/**
 * Check whether the data's key ID fetched from localStorage
 * belongs to the current visitor ID
 * @param {string} key
 *        Data key from localStorage
 * @returns {boolean}
 *        Whether the key ID matches the current visitor's ID
 *        or if there's no ID associated with the key
 * @private
 */
exports.checkKeyForVisitorId = function (key:string): boolean {
  //Get the current visitorId from Flux store first
  //Else if not existed, the get the new one from cookie
  //If `waitForOriginSync` hasn't been called, x-origin sync may come before or after snippet activation,
  //so we don't know whether the visitor ID is in the store or only in the cookie.
  var currentId = VisitorIdStore.getBucketingId() || exports.getCurrentId();
  var keyVisitorId = exports.getIdFromKey(key);
  //Return true also for key that does not contain any ID
  //Ex: https://www.abc.com$$pending_events
  return !keyVisitorId || keyVisitorId === currentId;
};

/**
 * @param {string} key
 *        A storage key.
 * @returns {null|string}
 *        The visitor ID within the key, if it contains one.
 */
exports.getIdFromKey = function getIdFromKey(key:string): null|string {
  // Not all keys in localStorage are managed by the Visitor module.
  // Call such keys 'non-Visitor keys'.
  //
  // firstPart is a string like one of the following:
  //   - 'https://example.com_other_thing', if foreign non-Visitor keys;
  //   - 'https://example.com_id123', if foreign Visitor keys; OR
  //   - 'id123', if local.
  var firstPart = key.split('$$')[0];

  var storageKey = exports.getStorageKeyFromKey(key);
  var isNonVisitorKey = _.includes(enums.StorageKeys, storageKey);
  if (isNonVisitorKey) {
    return null;
  }

  var start = firstPart.indexOf('_');
  var isLocalKey = start === -1;

  if (isLocalKey) {
    return firstPart;
  }

  return firstPart.substring(start + 1);
};

/**
 * @param {string} key
 * @returns {string|null}
 *        One of constants.enums.AllStorageKeys.
 *        If a specific key can't be identified, null.
 */
exports.getStorageKeyFromKey = function getStorageKeyFromKey(key:string): string|null {
  // lastPart is a string like one of the following:
  //   - 'key_name', for Visitor-managed keys; OR
  //   - 'https://example.com_key_name', for foreign non-Visitor keys
  var lastPart = key.split('$$').pop();

  var keyContainsOrigin = lastPart.indexOf('://') > -1;

  var maybeKey;
  if (keyContainsOrigin) {
    var separatorIndex = lastPart.indexOf('_');
    maybeKey = lastPart.substring(separatorIndex + 1);
  } else {
    maybeKey = lastPart;
  }

  return _.includes(_.values(enums.AllStorageKeys), maybeKey) ? maybeKey : null;
};

/**
 * Delete local data in LocalStorage that does not
 * belong to the current visitorId
 */
exports.deleteOldLocalData = function () {
  var storageKeys = LocalStorage.keys();
  _.each(storageKeys, function(key) {
    if (exports.isForeignKey(key)) {
      return;
    }
    if (exports.checkKeyForVisitorId(key)) {
      return;
    }
    LocalStorage.removeItem(key);
  });
};

/**
 * Delete all foreign data currently in origin's LocalStorage
 */
exports.deleteOldForeignData = function () {
  var storageKeys = LocalStorage.keys();
  _.each(storageKeys, function(key) {
    if (exports.isForeignKey(key)){
      LocalStorage.removeItem(key);
    }
  });
};

/**
 * Transform the visitorProfile for storage
 * @private
 */
function serializeVisitorProfile() {
  var visitorProfile = VisitorStore.getVisitorProfile();
  var metadata = VisitorStore.getVisitorProfileMetadata();
  var providers = PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders);

  // if providers are provided, filter transient providers
  if (providers) {
    var providerByKey = _.reduce(providers, function(map, provider) {
      if (provider.provides) {
        map[provider.provides] = provider;
      }

      return map;
    }, {});

    visitorProfile = _.omitBy(visitorProfile, function(value, key) {
      var provider = providerByKey[key];
      return provider && provider.isTransient;
    });
  }

  return {
    profile: visitorProfile,
    metadata: metadata,
  };
}

/**
 * Filter the visitor profile to only include sticky attributes
 * @param {Object} visitorProfile
 * @param {Object} visitorProfile.profile
 * @param {Object} visitorProfile.metadata
 * @returns {Object}
 */
function filterVisitorStickyProfile(visitorProfile) {
  const stickyProvides = _.flattenDeep(Object.values<VisitorProfileProvider>(
      PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders))
    .filter((plugin) => plugin.isSticky)
    .map((plugin) => plugin.provides));

 return {
    profile: _.omitBy(visitorProfile.profile, (value, key) => !stickyProvides.includes(key)),
    metadata: _.omitBy(visitorProfile.metadata, (value, key) => !stickyProvides.includes(key)),
  };
}

/**
 * Load store with visitorEvents
 * @param {BehaviorStoredEvents[]} visitorEvents
 * @param {BehaviorStoredEvents[]} visitorEventQueue
 * @private
 */
function loadBehaviorEvents(visitorEvents:BehaviorStoredEvents[], visitorEventQueue:BehaviorStoredEvents[]) {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('BehaviorStoredEvents', visitorEvents);
    require('../utils/invariant').isType('BehaviorStoredEvents', visitorEventQueue);
  }
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('loadBehaviorEvents');
  Behavior.initializeStore(visitorEvents, visitorEventQueue);
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('loadBehaviorEvents');
}

/**
 * Load data from synced foreign items into stores.
 */
exports.loadForeignData = function() {
  _.each(LocalStorage.keys(), function(key) {
    var value = LocalStorage.getItem(key);
    if (value) {
      maybeUpdateLocalStore(key, value);
    }
  });
};

/**
 * Load store with LayerStates
 * @param {LayerState[]} layerStates
 * @private
 */
function loadLayerStates(layerStates:LayerState[]) {
  Flux.dispatch(actionTypes.LOAD_PERSISTED_LAYER_STATES, {
    layerStates: _.filter(layerStates, function(layerState) {
      // Invalid legacy layerStates have no decision
      return !!layerState.decision;
    }),
  });
}

/**
 * Load store with SessionState
 * @param {Object} sessionState
 * @param {string} sessionState.sessionId
 * @param {number} sessionState.lastSessionTimestamp
 * @private
 */
function loadSessionState(sessionState:any) {
  // ensure there is a lastSessionTimestamp
  sessionState = _.extend({
    lastSessionTimestamp: 0,
    sessionId: null,
  }, sessionState);
  Flux.dispatch(actionTypes.LOAD_SESSION_STATE, sessionState);
}

/**
 * Retrieves the visitorId from the specified locator. If the locator is not found
 * (e.g. the specified cookie does not exist on the page), throw an error and abort.
 * @param {VisitorIdLocator} visitorIdLocator
 * @returns {string}
 * @private
 */
function generateVisitorIdFromLocator(visitorIdLocator:VisitorIdLocator): string {
  var visitorId;
  var key = visitorIdLocator.name;

  switch(visitorIdLocator.type) {
    case enums.VisitorIdLocatorType.COOKIE:
      visitorId = Cookie.get(key);
      break;
    case enums.VisitorIdLocatorType.JS_VARIABLE:
      visitorId = WindowEnv.getGlobalByPath(key);
      break;
    case enums.VisitorIdLocatorType.LOCALSTORAGE:
      try {
        var localStorage = WindowEnv.getGlobal('localStorage');
        visitorId = localStorage.getItem(key);
      } catch(e) {
        throw new Error('Unable to read localStorage: ' + e.toString());
      }
      break;
    case enums.VisitorIdLocatorType.QUERY:
      visitorId = Query.getQueryParamValue(key);
      break;
  }

  try {
    if (!visitorId) {
      Logger.error('Visitor / Customer provided visitor id cannot be found. Type:', visitorIdLocator.type, ' Name:', key);
      throw new Error('Failure to obtain visitor id from ' + visitorIdLocator.type);
    }

    if (!_.isString(visitorId) && !_.isNumber(visitorId)) {
      Logger.error('Visitor / Customer provided visitor id is not a string or number. Type:', visitorIdLocator.type, ' Name:', key, ' Id Type:', typeof(visitorId));
      throw new Error('Customer provided visitor id is not a string or number');
    }
  } catch(e) {
    if (RumStore.getSampleRum()) {
      Flux.dispatch(actionTypes.RECORD_VISITOR_ID_ERROR, {
        isError: true,
      });
    }
    throw e;
  }

  if (RumStore.getSampleRum()) {
    Flux.dispatch(actionTypes.RECORD_VISITOR_ID_ERROR, {
      isError: false,
    });

    Flux.dispatch(actionTypes.RECORD_VISITOR_ID_LOCATOR_USAGE, {
      visitorIdLocatorType: visitorIdLocator.type,
      entityId: visitorId,
    });
  }

  return String(visitorId);
}

/**
 * Generate a random visitor ID as in [user.getID](https://github.com/optimizely/optimizely/blob/06b84fe4c24d26712823dff967db3632093d64fd/src/www/js/client/user.js#L93)
 * @returns {string}
 * @private
 */
function generateRandomVisitorId(): string {
  return 'oeu' + DateEnv.now() + 'r' + Math.random();
}

/**
 * Load store with visitorProfile
 * @param {Object} visitorProfile
 * @param {Object} visitorProfile.profile
 * @param {Object} visitorProfile.metadata
 * @private
 */
function loadVisitorProfile(visitorProfile:any) {
  var providers = PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders);
  var restorableProviders = _.filter(providers, function(provider) {
    return _.isFunction(provider.restorer);
  });

  var profile;
  var metadata;
  if (visitorProfile.profile && visitorProfile.metadata) { // new format
    profile = visitorProfile.profile;
    metadata = visitorProfile.metadata;
  } else {
    profile = visitorProfile;
    metadata = {} as any;
  }
  profile = _.reduce(profile, function(res, val, provides) {
    var valueToLoad = val;
    var provider = _.find(restorableProviders, {provides: provides});
    if (provider) {
      valueToLoad = provider.restorer(val);
    }
    res[provides] = valueToLoad;
    return res;
  }, {});

  Flux.dispatch(actionTypes.LOAD_EXISTING_VISITOR_PROFILE, {
    profile: profile,
    metadata: metadata,
  });
}

/**
 * Get current namespace
 * @returns {string}
 */
exports.getNamespace = function(): string {
  return GlobalStore.getNamespace();
};

function safeParse(s) {
  try {
    return JSONEnv.parse(s);
  } catch(e) {
    Logger.debug('Failed to parse: ', s, e);
    return null;
  }
}

/**
 * @param {string|Array<string>} nameOrKeyPath
 *        Either a field name (e.g., 'browserId') or key path (e.g., ['custom', 'foo']).
 * @returns {string}
 *        A one-to-one representation of the given name or key path.
 *        Not perfectly true, since 'custom$$foo' and ['custom', 'foo'] would have the
 *        same serialization. We assume here that we never make profile property names
 *        with the special '$$' delimiter in them.
 */
exports.serializeFieldKey = function serializeFieldKey(nameOrKeyPath:string|string[]): string {
  if (!_.isArray(nameOrKeyPath)) {
    return nameOrKeyPath as string;
  }

  return (<string[]>nameOrKeyPath).join('$$');
};

if (__TEST__) {
  exports.generateVisitorIdFromLocator = generateVisitorIdFromLocator;
  exports.getVisitorDataKey = getVisitorDataKey;
  exports.loadData = loadData;
  exports.resolveConflicts = resolveConflicts;
  exports.getExperimentEdgeSPA = function() {
    return EXPERIMENTAL_EDGE_SPA;
  };
  exports.setExperimentEdgeSPA = function(experimentEdgeSPA) {
    EXPERIMENTAL_EDGE_SPA = experimentEdgeSPA;
  };
  exports.getExperimentWebSdk = function() {
    return EXPERIMENTAL_WEB_SDK;
  };
  exports.setExperimentWebSdk = function(experimentWebSdk) {
    EXPERIMENTAL_WEB_SDK = experimentWebSdk;
  };
}

/**
 * Remove any optimizelySessionState cookies that may have been used for session state.
 *
 * More info at
 * https://confluence.sso.episerver.net/display/EXPENG/Persisting+session+state+in+client-js
 */
exports.removeLegacySessionStateCookies = function() {
  var cookies = Cookie.getAll();
  _.forEach(_.keys(cookies), function(cookieName) {
    if (cookieName.indexOf(enums.COOKIES.SESSION_STATE + '$$') === 0) {
      Cookie.remove(cookieName);
    }
  });
};
